import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Crown, Lock, Zap } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const useSubscription = () => {
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadSubscription();
  }, []);

  const loadSubscription = async () => {
    const token = localStorage.getItem('session_token');
    if (!token) {
      setLoading(false);
      return;
    }

    try {
      const response = await axios.get(`${API}/subscription`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setSubscription(response.data);
    } catch (error) {
      console.error('Failed to load subscription:', error);
    } finally {
      setLoading(false);
    }
  };

  const hasFeature = (feature) => {
    if (!subscription || !subscription.is_active) return false;
    return subscription.features?.[feature] === true;
  };

  const hasFramework = (frameworkId) => {
    if (!subscription || !subscription.is_active) return false;
    return subscription.features?.frameworks?.includes(frameworkId?.toLowerCase());
  };

  const hasRiskMatrix = (matrixType) => {
    if (!subscription || !subscription.is_active) return false;
    return subscription.features?.risk_matrix?.includes(matrixType);
  };

  const isPro = () => subscription?.tier === 'pro' && subscription?.is_active;
  const isLite = () => subscription?.tier === 'lite' && subscription?.is_active;
  const isActive = () => subscription?.is_active === true;
  const requiresPayment = () => subscription?.requires_payment === true;

  return {
    subscription,
    loading,
    hasFeature,
    hasFramework,
    hasRiskMatrix,
    isPro,
    isLite,
    isActive,
    requiresPayment,
    refetch: loadSubscription
  };
};

export const UpgradePrompt = ({ feature, title, description, className = '' }) => {
  return (
    <Card className={`p-8 text-center bg-gradient-to-br from-primary/5 to-accent/5 border-primary/20 ${className}`}>
      <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary/10 mb-4">
        <Lock className="h-8 w-8 text-primary" />
      </div>
      <h3 className="text-xl font-semibold mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
        {title || 'Pro Feature'}
      </h3>
      <p className="text-muted-foreground mb-6 max-w-md mx-auto">
        {description || `This feature is available with a Pro subscription. Upgrade to unlock ${feature}.`}
      </p>
      <Link to="/pricing">
        <Button className="gap-2">
          <Crown className="h-4 w-4" />
          Upgrade to Pro
        </Button>
      </Link>
    </Card>
  );
};

export const PaymentRequiredPrompt = () => {
  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4">
      <Card className="p-8 text-center max-w-md w-full">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary/10 mb-4">
          <Crown className="h-8 w-8 text-primary" />
        </div>
        <h2 className="text-2xl font-bold mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
          Choose Your Plan
        </h2>
        <p className="text-muted-foreground mb-6">
          Select a subscription plan to start managing your compliance frameworks.
        </p>
        <Link to="/pricing">
          <Button className="w-full gap-2" size="lg">
            <Crown className="h-4 w-4" />
            View Plans & Subscribe
          </Button>
        </Link>
      </Card>
    </div>
  );
};

export const UpgradeBadge = ({ onClick }) => {
  return (
    <button
      onClick={onClick}
      className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-gradient-to-r from-primary/20 to-accent/20 text-primary border border-primary/30 hover:from-primary/30 hover:to-accent/30 transition-all"
    >
      <Crown className="h-3 w-3" />
      PRO
    </button>
  );
};

export const FeatureGate = ({ feature, frameworkId, children, upgradeTitle, upgradeDescription }) => {
  const { hasFeature, hasFramework, loading } = useSubscription();

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  const hasAccess = frameworkId ? hasFramework(frameworkId) : hasFeature(feature);

  if (!hasAccess) {
    return (
      <UpgradePrompt
        feature={feature || frameworkId}
        title={upgradeTitle}
        description={upgradeDescription}
      />
    );
  }

  return <>{children}</>;
};

export const TierBadge = ({ tier }) => {
  if (tier === 'pro') {
    return (
      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-gradient-to-r from-yellow-500/20 to-orange-500/20 text-yellow-600 dark:text-yellow-400 border border-yellow-500/30">
        <Crown className="h-3 w-3" />
        PRO
      </span>
    );
  }

  return (
    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-primary/10 text-blue-600 dark:text-primary border border-primary/20">
      <Zap className="h-3 w-3" />
      LITE
    </span>
  );
};
